import { useState, type ReactNode } from 'react';

import { Modal } from '@/components/Modal';

/**
 * 확대 창 안의 <b>이니셜 칸 크기</b>. 사진이 없는 사람도 같은 크기의 칸을 보게 해, 창이 사람마다
 * 들쭉날쭉하지 않다. 부르는 쪽이 이 값으로 자기 이니셜 칸을 한 번 더 그려 넘긴다.
 */
export const ZOOM_INITIAL_SIZE = 240;

/**
 * <b>눌러서 크게 보는</b> 증명사진 (사용자 지시 2026-09-28: 「프로필 사진 클릭시 확대 팝업창 필요 —
 * 식별불가」).
 *
 * <p>사진이 서는 자리는 셋이고 전부 작다 — 직원찾기 명함 76×96, 내정보 64×80, 댓글 28×28. 올라온
 * 파일 자체는 그보다 훨씬 크고 화면이 CSS 로 줄여 놓은 것이라, <b>같은 링크를 크게 그리는 것만으로</b>
 * 얼굴이 식별된다. 큰 사진을 따로 만들거나 서버에 새 경로를 두지 않는다.
 *
 * <h2>사진이 없는 사람도 눌린다 (사용자 지시 2026-09-28 · 두 번째)</h2>
 * 처음에는 이니셜 칸을 못 누르게 뒀다 — 글자 한 자를 키워도 얼굴이 식별되지는 않으니까. 그런데
 * <b>누를 수 있는 자리가 사람마다 달라지는 것</b>이 그 자체로 불편하다: 같은 목록에서 어떤 얼굴은
 * 커지고 어떤 얼굴은 눌러도 아무 일이 없다. 그래서 이니셜도 같은 창을 열고, 안에서는 글자를 크게
 * 세운다({@link ZOOM_INITIAL_SIZE}).
 *
 * <h2>레이아웃을 건드리지 않는다</h2>
 * 감싸는 버튼은 여백·테두리·줄높이를 다 지운다. 세 자리 모두 가로줄(flex) 안의 칸이라, 감싸는 것이
 * 자리를 차지하면 이름·연락처가 밀린다. 크기와 모양은 부르는 쪽이 준 것이 그대로 정한다 — 이 파일은
 * <b>누를 수 있게 만드는 일</b>만 한다.
 */
export function ZoomablePhoto({
  src,
  name,
  style,
  initial,
  initialLarge,
}: {
  /** 증명사진 링크. 없으면 {@link initial} 이 그 자리에 선다. */
  src: string | null;
  /** 누구의 사진인가 — 확대 창의 제목과 대체 글이 된다. */
  name: string;
  /** 사진일 때 작게 보일 크기·모양. 부르는 쪽이 쓰던 값을 그대로 넘긴다. */
  style?: React.CSSProperties;
  /** 사진이 없을 때 작게 보일 것(부르는 쪽의 이니셜 칸). 없으면 아무것도 그리지 않는다. */
  initial?: ReactNode;
  /** 같은 이니셜 칸을 {@link ZOOM_INITIAL_SIZE} 크기로 그린 것. 확대 창 안에 선다. */
  initialLarge?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const label = src ? `${name} 증명사진` : `${name} 프로필`;

  if (!src && initial === undefined) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`${label} 크게 보기`}
        title="크게 보기"
        style={{
          padding: 0,
          border: 'none',
          background: 'none',
          lineHeight: 0,
          flexShrink: 0,
          cursor: 'zoom-in',
        }}
      >
        {src ? <img src={src} alt={label} style={style} /> : initial}
      </button>

      {open && (
        <Modal title={label} onClose={() => setOpen(false)} maxWidth="560px">
          {src ? (
            /*
              창 높이의 70% 까지만 키운다 — 세로로 긴 증명사진을 폭에만 맞추면 아래쪽이 창 밖으로
              나가고, 얼굴을 보려고 모달 안에서 다시 굴려야 한다.
            */
            <img
              src={src}
              alt={label}
              style={{
                display: 'block',
                margin: '0 auto',
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: '8px',
                border: '1px solid var(--border)',
              }}
            />
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>{initialLarge}</div>
          )}
        </Modal>
      )}
    </>
  );
}
